import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface PendingNavigation {
  href: string;
  internalPath: string | null;
}

interface UseH3ightboardUnsavedChangesGuardOptions {
  hasUnsavedChanges: boolean;
  canSave: boolean;
  isSaving: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
}

export interface H3ightboardUnsavedChangesDialogProps {
  open: boolean;
  canSave: boolean;
  isSaving: boolean;
  onStay: () => void;
  onDiscardAndLeave: () => void;
  onSaveAndLeave: () => void;
}

function sameRoute(locationPath: string, nextPath: string): boolean {
  return locationPath === nextPath;
}

export function useH3ightboardUnsavedChangesGuard({
  hasUnsavedChanges,
  canSave,
  isSaving,
  onSave,
  onDiscard,
}: UseH3ightboardUnsavedChangesGuardOptions): H3ightboardUnsavedChangesDialogProps {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const [leaveAfterSave, setLeaveAfterSave] = useState(false);
  const suppressNavigationGuard = useRef(false);

  const currentPath = `${location.pathname}${location.search}${location.hash}`;

  const resetBlockedNavigation = useCallback(() => {
    setPendingNavigation(null);
    setLeaveAfterSave(false);
  }, []);

  const proceedWithNavigation = useCallback(
    (navigation: PendingNavigation) => {
      suppressNavigationGuard.current = true;
      setPendingNavigation(null);
      setLeaveAfterSave(false);

      if (navigation.internalPath !== null) {
        navigate(navigation.internalPath);
      } else {
        window.location.assign(navigation.href);
      }

      window.setTimeout(() => {
        suppressNavigationGuard.current = false;
      }, 0);
    },
    [navigate],
  );

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (suppressNavigationGuard.current || !hasUnsavedChanges || event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.hasAttribute('download')) return;
      if (anchor.target && anchor.target !== '_self') return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      const nextUrl = new URL(anchor.href, window.location.href);
      const sameOrigin = nextUrl.origin === window.location.origin;
      const internalPath = sameOrigin ? `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}` : null;

      if (internalPath !== null && sameRoute(currentPath, internalPath)) return;

      event.preventDefault();
      setPendingNavigation({ href: nextUrl.href, internalPath });
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [currentPath, hasUnsavedChanges]);

  useEffect(() => {
    if (hasUnsavedChanges || !pendingNavigation) return;

    const timer = window.setTimeout(() => {
      if (leaveAfterSave) {
        proceedWithNavigation(pendingNavigation);
        return;
      }

      resetBlockedNavigation();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [hasUnsavedChanges, leaveAfterSave, pendingNavigation, proceedWithNavigation, resetBlockedNavigation]);

  const handleStay = useCallback(() => {
    resetBlockedNavigation();
  }, [resetBlockedNavigation]);

  const handleDiscardAndLeave = useCallback(() => {
    if (!pendingNavigation) return;
    onDiscard?.();
    proceedWithNavigation(pendingNavigation);
  }, [onDiscard, pendingNavigation, proceedWithNavigation]);

  const handleSaveAndLeave = useCallback(() => {
    if (!canSave || !onSave) return;
    setLeaveAfterSave(true);
    onSave();
  }, [canSave, onSave]);

  return {
    open: pendingNavigation !== null,
    canSave,
    isSaving,
    onStay: handleStay,
    onDiscardAndLeave: handleDiscardAndLeave,
    onSaveAndLeave: handleSaveAndLeave,
  };
}
